import React from 'react';
import { Tag, Button, Tooltip, Modal, notification } from 'antd';
import {
  EyeOutlined,
  InfoCircleFilled,
  WarningOutlined,
  ShoppingCartOutlined,
  DollarOutlined,
  CarOutlined,
  HomeOutlined,
  CreditCardOutlined
} from '@ant-design/icons';
import moment from 'moment';
import { extractTimeFromOrder, hasSchedulingIssue, formatPrice, getStatusColor } from '../../../utils/orderUtils';
import { getOnfleetImageUrls, getOrderCompletionDetails, getOrderCompletionTime, getTrackingInfo } from '../../../utils/onfleetUtils';
import DeliveryImageCarousel from './DeliveryImageCarousel';
import { showOnfleetCompletionModal } from './OnfleetCompletionModal';
import OrderTaskNoteButton from './OrderTaskNoteButton';
import {
  canCaptureSavedCard as isSavedCardCaptureEligible,
  getSavedCardActionLabel,
  getSavedCardActionTooltip,
} from '../../../utils/orderPaymentAction';

const OrderCard = ({ 
  order, 
  onViewDetails, 
  onEditItems, 
  onEditDateTime, 
  onCaptureSavedCard,
  onNoteSaved,
  isAdmin = false,
  CalendarIcon,
  showSchedulingWarnings = false
}) => {
  const { arrivalTime, completionTime } = extractTimeFromOrder(order);
  const hasSchedulingProblem = hasSchedulingIssue(order);
  const statusColor = getStatusColor(order.order_status);
  const headerClass = 'bg-blue-50 border-blue-100';
  const canCaptureSavedCard = Boolean(
    onCaptureSavedCard
    && isSavedCardCaptureEligible(order)
  );
  const savedCardActionLabel = getSavedCardActionLabel(order);
  const completionDetails = getOrderCompletionDetails(order);
  const deliveryImages = getOnfleetImageUrls(completionDetails);
  const hasCompletionInfo = Boolean(completionDetails || getOrderCompletionTime(order));
  
  // Check if order has tracking info
  const hasTrackingInfo = () => {
    // For en-route, completed or collected orders with task_id
    if (order.task_id && 
        (order.order_status === 'En-route' || 
         order.order_status === 'On-Route' ||
         order.order_status === 'Completed' || 
         order.order_status === 'Collected')) {
      
      // Try to parse task_info if it exists
      if (order.task_info) {
        try {
          const taskInfo = JSON.parse(order.task_info);
          return Boolean(taskInfo.shortId || taskInfo.trackingURL);
        } catch (error) {
          console.error("Error parsing task_info:", error);
        }
      }
       
      // Even without task_info, if we have a task_id, we can show tracking
      return true;
    }
    
    return false;
  };
  
  // Format address properly
  const formatAddress = () => {
    try {
      const propertyTitle = order.order_property?.property_title || 'N/A';
      const apartmentNumber = order.order_property?.apartment_number || '';
      const address = order.order_property?.apartment_address || order.delivery_address_1 || '';
      const city = order.delivery_city || order.order_property?.delivery_city || '';
      const zipCode = order.delivery_zip_code || order.order_property?.delivery_zip_code || '';
      
      const parts = [apartmentNumber, address, city, zipCode].filter(Boolean);
      return parts.join(', ');
    } catch (error) {
      return "Address unavailable";
    }
  };

  const formatCompletionTime = (value) => {
    if (!value) return null;
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format('DD MMM YYYY, HH:mm') : String(value);
  };
  
  // Show tracking modal
  const showTrackingModal = () => {
    const { finalTrackingUrl } = getTrackingInfo(order);
    const completionDetails = getOrderCompletionDetails(order);
    const onfleetCompletionTime = getOrderCompletionTime(order);
    const statusDisplayTime = onfleetCompletionTime || order.updated_at;
    const deliveryImages = getOnfleetImageUrls(completionDetails);
    
    if (!finalTrackingUrl && !order.task_id) {
      notification.info({
        message: 'Tracking Unavailable',
        description: 'Tracking information is not available for this order.'
      });
      return;
    }
    
    Modal.info({
      title: `Tracking Information - Order #${order.id}`,
      width: 600,
      content: (
        <div className="p-4">
          <div className="mb-4">
            <h3 className="text-lg font-medium mb-2">
              Order #{order.id} - {order.order_status}
            </h3>
            <p className="text-gray-500">
              {order.order_status === 'Completed' ? 'Delivery completed' : 
               order.order_status === 'Collected' ? 'Items collected' : 
               'Currently in progress'} 
              {statusDisplayTime ? ` on: ${formatCompletionTime(statusDisplayTime)}` : ''}
            </p>
          </div>
          
          <div>
            {finalTrackingUrl ? (
              <>
                <p className="mb-3">Tracking information is available for this order.</p>
                <div className="bg-gray-50 p-3 rounded border mb-4">
                  <p className="font-medium mb-2">Tracking URL:</p>
                  <a 
                    href={finalTrackingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:text-blue-700 flex items-center break-all"
                  >
                    {finalTrackingUrl}
                  </a>
                </div>
              </>
            ) : (
              <p className="mb-3">This order is being tracked but the tracking link is not available. 
              The tracking ID is: {order.task_id}</p>
            )}
          </div>
          
          {order.driver_name && isAdmin && (
            <div className="mt-4 pt-4 border-t">
              <h4 className="font-medium mb-2">Delivery Details</h4>
              <p><strong>Driver:</strong> {order.driver_name}</p>
              {order.driver_phone && (
                <p><strong>Contact:</strong> {order.driver_phone}</p>
              )}
            </div>
          )}
          
          {(completionDetails || onfleetCompletionTime) && (
            <div className="mt-4 pt-4 border-t">
              <h4 className="font-medium mb-2">Completion Details</h4>
              {completionDetails && (
                <p><strong>Status:</strong> {completionDetails.success === false ? 'Failed' : 'Success'}</p>
              )}
              {onfleetCompletionTime && (
                <p><strong>Delivered:</strong> {formatCompletionTime(onfleetCompletionTime)}</p>
              )}
              {completionDetails?.notes && <p><strong>Notes:</strong> {completionDetails.notes}</p>}
              {deliveryImages.length > 0 && (
                <div className="mt-3">
                  <p className="font-medium mb-2">Delivery Images:</p>
                  <DeliveryImageCarousel images={deliveryImages} maxHeight={300} />
                </div>
              )}
            </div>
          )}
        </div>
      ),
      okText: 'Close',
      cancelText: finalTrackingUrl ? 'View on Onfleet' : null,
      onCancel: finalTrackingUrl ? () => {
        window.open(finalTrackingUrl, '_blank');
        return Promise.resolve();
      } : null
    });
  };
  
  // Get items count
  const getItemsCount = () => {
    try {
      if (order.order_items) {
        return order.order_items;
      }
      
      if (order.order_products && Array.isArray(order.order_products)) {
        return order.order_products.length;
      }
      
      return 0;
    } catch (error) {
      return 0;
    }
  };
  
  // Show ETA for en-route orders
  const showOnfleetInfo = () => {
  if (
    (order.order_status === 'Completed' || order.order_status === 'Collected') &&
    (order.task_info || order.completion_details || order.onfleet_status_time)
  ) {
    const completionDetails = getOrderCompletionDetails(order);
    const completionTime = getOrderCompletionTime(order);
    const deliveryImages = getOnfleetImageUrls(completionDetails);

      const completionStatus = completionDetails
        ? (completionDetails.success === false ? 'Failed' : 'Success')
        : order.order_status;
      const completionStamp = completionTime
        ? moment(completionTime).format('DD MMM YYYY, HH:mm')
        : null;

      return (
        <div className="mt-2 text-xs text-gray-600 leading-snug border-t pt-2">
          <p>
            <strong>Onfleet:</strong> {[completionStatus, completionStamp].filter(Boolean).join(' | ')}
          </p>
          {completionDetails?.notes && <p><strong>Notes:</strong> {completionDetails.notes}</p>}
          {deliveryImages.length > 0 && <p>{deliveryImages.length} delivery image{deliveryImages.length > 1 ? 's' : ''} available</p>}
        </div>
      );
  }

  // fallback for in-progress ETA
  if (
    (order.order_status === 'En-route' || order.order_status === 'On-Route') &&
    order.eta
  ) {
    return (
      <div className="mt-1 flex items-center text-xs">
        <CarOutlined className="mr-1 text-blue-500" />
        ETA: {order.eta}
      </div>
    );
  }

  return null;
};

  
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden mb-4">
      <div className={`${headerClass} text-gray-900 border-b p-3`}>
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-between sm:items-center mb-2">
          <div className="flex items-center min-w-0 shrink-0">
            <div className="bg-white border border-gray-200 text-golden-navy rounded-full mr-1 p-2 shadow-sm">
              <HomeOutlined className="h-4 w-4" />
            </div>
            <h3 className="text-xl font-semibold">#{order.id}</h3>
          </div>
          <div className="flex items-center justify-between sm:justify-end sm:ml-auto gap-2">
            <span className="font-bold text-sm sm:text-base whitespace-nowrap">TOTAL: £{formatPrice(order.order_total)}</span>
            <OrderTaskNoteButton order={order} onSaved={onNoteSaved} />
            <Button 
              type="text"
              onClick={() => onViewDetails(order.id)}
              className="text-golden-navy hover:text-blue-700 p-0"
              icon={<EyeOutlined className="text-lg" />}
            />
          </div>
          
        </div>
      
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-end mb-1 w-full">
        <div className="flex flex-col items-start sm:mr-3 w-full">
              {/* Status Tag with proper styling */}
              <div>
                <Tag color={statusColor}>{order.order_status}</Tag>
                {hasSchedulingProblem && showSchedulingWarnings && (
                  <Tooltip title="This order is not properly scheduled. Missing task ID." trigger={['hover', 'focus', 'click']}>
                    <span
                      role="img"
                      aria-label="Scheduling warning: missing task ID"
                      tabIndex={0}
                      className="inline-flex items-center text-orange-500 align-middle"
                    >
                      <WarningOutlined aria-hidden="true" />
                    </span>
                  </Tooltip>
                )}
                {order.onfleet_task_position > 0 && order.order_status== "Scheduled" &&
                <i style={{ marginLeft: '5px',fontSize: '8px' ,width: '100%' }}>
           <br/>Orders in Queue: {order.onfleet_task_position}
        </i>}
              </div>
            </div>
            <div className="flex flex-col items-start sm:items-end w-full">
              {/* Show tracking button for relevant orders */}
              {(hasCompletionInfo || hasTrackingInfo()) && (
                <>
                  <div className="mt-1">
                    <Button 
                      type="link" 
                      size="small" 
                      onClick={() => hasCompletionInfo
                        ? showOnfleetCompletionModal(order, { isAdmin })
                        : showTrackingModal()}
                      className="p-0 text-xs text-blue-500 hover:text-blue-700"
                    >
                      <InfoCircleFilled className="mr-1" />
                      {hasCompletionInfo
                        ? `Details${deliveryImages.length ? ` / Photos (${deliveryImages.length})` : ''}`
                        : 'View Tracking'}
                    </Button>
                  </div>
                  {showOnfleetInfo()}
                </>
              )}
              
            </div>
          </div>
      </div>
      
      <div className="p-3 sm:p-4">
        <div className="flex-grow">
          <div className="flex justify-between items-start mb-3">
            <div className="min-w-0">
              <p className="font-semibold text-lg">
                {order.order_property && order.order_property.property_title ? 
                 order.order_property.property_title : 'N/A'} 
              </p>
              <p className="text-sm text-gray-600">{formatAddress()}</p>
            </div>
            
          
          </div>
          
        
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600 mb-2">
          <div>
            <p><strong>Date:</strong> {order.property_date ? new Date(order.property_date).toLocaleDateString() : 'N/A'}</p>
            <p><strong>Time:</strong> {arrivalTime || 'N/A'} - {completionTime || 'N/A'}</p>
          </div>
          <div className="sm:text-right">
            <p><strong>Items:</strong> {getItemsCount()}</p>
            <p><strong>Customer:</strong> {order.delivery_first_name || ''} {order.delivery_last_name || ''}</p>
          </div>
        </div>
        
        {/* Payment status indicator */}
        <div className="mt-2 flex items-center">
          <span className="text-sm font-medium mr-2">Payment:</span>
          {order.payment_status === 1 || order.payment_status === '1' ? (
            <Tag color="green"><DollarOutlined /> Paid</Tag>
          ) : (
            <Tag color="orange"><DollarOutlined /> Unpaid</Tag>
          )}
          {canCaptureSavedCard && (
            <Tooltip title={getSavedCardActionTooltip(order)}>
              <Button
                type="primary"
                size="small"
                icon={<CreditCardOutlined />}
                onClick={() => onCaptureSavedCard(order.id, order)}
                className="ml-2"
              >
                {savedCardActionLabel}
              </Button>
            </Tooltip>
          )}
        </div>
        
        {/* Admin-only edit buttons */}
        {isAdmin && (
          <div className="flex flex-col sm:flex-row sm:justify-end mt-3 gap-2 border-t pt-3">
            <Button
              type="text"
              icon={<ShoppingCartOutlined />}
              onClick={() => onEditItems(order.id)}
              className="text-purple-500"
            >
              Edit Items
            </Button>
            <Button
              type="text"
              icon={CalendarIcon && <CalendarIcon className="mr-1" />}
              onClick={() => {
                onEditDateTime(order.id, {
                  arrival_time: arrivalTime,
                  completion_time: completionTime,
                  order_date: order.property_date
                });
              }}
              className="text-green-500"
            >
              Edit Times
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderCard;
